import React, { useCallback, useEffect, useState } from 'react';
import { Button, Spinner, Toast, Select, TextInput } from '../../lib';
import apiServices from '../../services/apiServices';
import {
  AssignPortalRole,
  CreatePortalUser,
  CreatePortalUserResponse,
  GetPortalRoleAssignments,
  GetPortalRoleAudit,
  PortalAuditEntry,
  PortalRoleAssignmentsResponse,
  PortalRoleMemberHit,
  PortalUserActionResponse,
  RemovePortalUser,
  ResetPortalUserPassword,
  RevokePortalRole,
  SearchPortalRoleMembers,
} from '../../services/dtos';

// Superuser-grade roles get a typed confirmation, not just a click.
const SUPERUSER_ROLES = ['Admin', 'LocalUnion'];

// Server dates arrive as "yyyy-MM-dd HH:mm:ss" in UTC (JsConfig.AlwaysUseUtc)
function parseUtc(value?: string): Date | null {
  if (!value) return null;
  const d = new Date(
    value.replace(' ', 'T') + (value.endsWith('Z') ? '' : 'Z')
  );
  return isNaN(d.getTime()) ? null : d;
}

function fmtDate(value?: string): string {
  const d = parseUtc(value);
  return d ? d.toLocaleDateString() : '';
}

function fmtDateTime(value?: string): string {
  const d = parseUtc(value);
  return d ? d.toLocaleString() : '';
}

// Global-admin screen for the portal role system: role grants (FrontDesk,
// BusinessRep, ..., including Admin/LocalUnion with a typed confirm) and
// non-member user creation. Roles live in PortalRoleAssignment — the single
// authorization authority; every grant/revoke is audited server-side.
// (member and Steward are derived, never granted — so never listed here.)
const RoleAdminPage: React.FC = () => {
  const client = apiServices.client;

  const [data, setData] = useState<PortalRoleAssignmentsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [includeRevoked, setIncludeRevoked] = useState(false);

  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<PortalRoleMemberHit[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<PortalRoleMemberHit | null>(null);
  const [roleName, setRoleName] = useState('');
  const [saving, setSaving] = useState(false);

  const [audit, setAudit] = useState<PortalAuditEntry[] | null>(null);
  const [auditOpen, setAuditOpen] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [cEmail, setCEmail] = useState('');
  const [cFirst, setCFirst] = useState('');
  const [cLast, setCLast] = useState('');
  const [cPassword, setCPassword] = useState('');
  const [creating, setCreating] = useState(false);
  const [createNote, setCreateNote] = useState('');
  const [actionNote, setActionNote] = useState('');

  const load = useCallback(
    (revoked: boolean) => {
      setLoading(true);
      setError('');
      const req = new GetPortalRoleAssignments();
      req.includeRevoked = revoked;
      client
        .get(req)
        .then((res: PortalRoleAssignmentsResponse) => {
          setData(res);
          setLoading(false);
        })
        .catch((e: any) => {
          setError(e?.responseStatus?.message || 'Failed to load role assignments');
          setLoading(false);
        });
    },
    [client]
  );

  useEffect(() => {
    load(includeRevoked);
  }, [load, includeRevoked]);

  // debounced member search
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) {
      setHits([]);
      return;
    }
    const t = setTimeout(() => {
      setSearching(true);
      const req = new SearchPortalRoleMembers();
      req.q = q;
      client
        .get(req)
        .then((res: PortalRoleMemberHit[]) => {
          setHits(res);
          setSearching(false);
        })
        .catch(() => setSearching(false));
    }, 300);
    return () => clearTimeout(t);
  }, [query, client]);

  const assign = () => {
    if (!selected || !roleName) return;
    if (SUPERUSER_ROLES.includes(roleName)) {
      // eslint-disable-next-line no-restricted-globals
      const typed = window.prompt(
        `${roleName} is a superuser-grade role: it grants access to everything, ` +
          `including this screen. Type ${roleName} to confirm granting it to ${selected.name}.`
      );
      if (typed !== roleName) return;
    }
    setSaving(true);
    setError('');
    const req = new AssignPortalRole();
    req.uniqueMemberId = selected.uniqueMemberId;
    req.roleName = roleName;
    client
      .post(req)
      .then((res: PortalRoleAssignmentsResponse) => {
        setData(res);
        setSaving(false);
        setSelected(null);
        setQuery('');
        setRoleName('');
        if (includeRevoked) load(true);
        if (auditOpen) loadAudit();
      })
      .catch((e: any) => {
        setError(e?.responseStatus?.message || 'Failed to assign role');
        setSaving(false);
      });
  };

  const revoke = (assignmentId: number, label: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Revoke ${label}?`)) return;
    setError('');
    const req = new RevokePortalRole();
    req.assignmentId = assignmentId;
    client
      .post(req)
      .then((res: PortalRoleAssignmentsResponse) => {
        setData(res);
        if (includeRevoked) load(true);
        if (auditOpen) loadAudit();
      })
      .catch((e: any) => {
        setError(e?.responseStatus?.message || 'Failed to revoke role');
      });
  };

  const createUser = () => {
    if (!cEmail.trim() || !cFirst.trim() || !cLast.trim() || !cPassword) return;
    setCreating(true);
    setError('');
    setCreateNote('');
    const req = new CreatePortalUser();
    req.email = cEmail.trim();
    req.firstName = cFirst.trim();
    req.lastName = cLast.trim();
    req.password = cPassword;
    client
      .post(req)
      .then((res: CreatePortalUserResponse) => {
        setCreating(false);
        setCreateNote(
          res.reactivated
            ? `Reactivated ${res.name} — login unlocked with the new password.`
            : `Created ${res.name} — they're selected above; pick a role to grant access.`
        );
        setSelected({
          uniqueMemberId: res.uniqueMemberId,
          name: res.name,
          email: res.email,
          membershipStatus: 'Non-Member',
        } as PortalRoleMemberHit);
        setCEmail('');
        setCFirst('');
        setCLast('');
        setCPassword('');
      })
      .catch((e: any) => {
        setError(e?.responseStatus?.message || 'Failed to create user');
        setCreating(false);
      });
  };

  const resetPassword = () => {
    if (!selected) return;
    // eslint-disable-next-line no-restricted-globals
    const pw = window.prompt(`New password for ${selected.name} (8+ characters):`);
    if (pw === null) return;
    if (pw.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }
    setError('');
    const req = new ResetPortalUserPassword();
    req.uniqueMemberId = selected.uniqueMemberId;
    req.newPassword = pw;
    client
      .post(req)
      .then((res: PortalUserActionResponse) => {
        setActionNote(res.message);
        if (auditOpen) loadAudit();
      })
      .catch((e: any) =>
        setError(e?.responseStatus?.message || 'Failed to reset password')
      );
  };

  const removeUser = () => {
    if (!selected) return;
    // eslint-disable-next-line no-restricted-globals
    const typed = window.prompt(
      `This locks ${selected.name}'s login and revokes all their roles ` +
        `(recreate the same email later to reactivate). Type REMOVE to confirm.`
    );
    if (typed !== 'REMOVE') return;
    setError('');
    const name = selected.name;
    const req = new RemovePortalUser();
    req.uniqueMemberId = selected.uniqueMemberId;
    client
      .post(req)
      .then((res: PortalRoleAssignmentsResponse) => {
        setData(res);
        setActionNote(`${name} removed — login locked, roles revoked.`);
        setSelected(null);
        setQuery('');
        if (includeRevoked) load(true);
        if (auditOpen) loadAudit();
      })
      .catch((e: any) =>
        setError(e?.responseStatus?.message || 'Failed to remove user')
      );
  };

  const loadAudit = useCallback(() => {
    client
      .get(new GetPortalRoleAudit())
      .then((res: PortalAuditEntry[]) => setAudit(res))
      .catch(() => setAudit([]));
  }, [client]);

  const toggleAudit = () => {
    const next = !auditOpen;
    setAuditOpen(next);
    if (next && audit === null) loadAudit();
  };

  return (
    <div className="mx-auto w-full flex flex-col gap-6 dark:text-white">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Portal Role Admin
        </h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
          The portal&apos;s single authorization authority: role grants for
          members and staff, and non-member user accounts. Member access and
          Steward access are automatic (registration and job assignments) and
          are not managed here. Every grant and revoke is recorded permanently.
        </p>
      </div>

      {error && <Toast color="failure">{error}</Toast>}

      {/* Assign */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <h2 className="mb-3 text-lg font-medium text-gray-900 dark:text-white">
          Manage users &amp; roles
        </h2>
        <div className="flex flex-col gap-3 md:flex-row md:items-start">
          <div className="relative w-full md:w-80">
            <TextInput
              placeholder="Search member by name or email…"
              value={selected ? selected.name : query}
              disabled={!!selected}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setQuery(e.target.value)
              }
            />
            {selected && (
              <button
                className="absolute right-2 top-2.5 text-sm text-blue-600 hover:underline dark:text-blue-400"
                onClick={() => {
                  setSelected(null);
                  setQuery('');
                }}
              >
                change
              </button>
            )}
            {!selected && (searching || hits.length > 0) && (
              <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg dark:border-gray-700 dark:bg-gray-800">
                {searching && (
                  <div className="p-2">
                    <Spinner size="sm" color="info" />
                  </div>
                )}
                {hits.map((h) => (
                  <button
                    key={h.uniqueMemberId}
                    className="block w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-700"
                    onClick={() => {
                      setSelected(h);
                      setHits([]);
                    }}
                  >
                    <span className="font-medium">{h.name}</span>
                    <span className="ml-2 text-gray-500 dark:text-gray-400">
                      {h.email} · {h.membershipStatus}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <Select
            className="w-full md:w-56"
            value={roleName}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
              setRoleName(e.target.value)
            }
          >
            <option value="">Select role…</option>
            {data?.availableRoles?.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
          </Select>

          <Button
            color="info"
            disabled={!selected || !roleName || saving}
            onClick={assign}
          >
            {saving ? <Spinner size="sm" /> : 'Assign'}
          </Button>
        </div>
        {selected && selected.membershipStatus === 'Non-Member' && (
          <div className="mt-3 flex gap-4 text-sm">
            <button
              className="text-blue-600 hover:underline dark:text-blue-400"
              onClick={resetPassword}
            >
              Reset password
            </button>
            <button
              className="text-red-600 hover:underline dark:text-red-400"
              onClick={removeUser}
            >
              Remove user
            </button>
          </div>
        )}
        {actionNote && (
          <p className="mt-2 text-sm text-green-700 dark:text-green-400">
            {actionNote}
          </p>
        )}
      </div>

      {/* Create non-member user */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <button
          className="text-lg font-medium text-gray-900 hover:underline dark:text-white"
          onClick={() => setShowCreate(!showCreate)}
        >
          {showCreate ? 'Hide create user' : 'Create a non-member user'}
        </button>
        {showCreate && (
          <div className="mt-3 flex flex-col gap-3">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              For office staff and others who aren&apos;t members: creates a
              login plus a portal identity so roles can be granted like anyone
              else. Members never need this — registration creates their
              account.
            </p>
            <div className="flex flex-col gap-3 md:flex-row">
              <TextInput
                className="w-full md:w-64"
                placeholder="Email"
                type="email"
                value={cEmail}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCEmail(e.target.value)
                }
              />
              <TextInput
                className="w-full md:w-40"
                placeholder="First name"
                value={cFirst}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCFirst(e.target.value)
                }
              />
              <TextInput
                className="w-full md:w-40"
                placeholder="Last name"
                value={cLast}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCLast(e.target.value)
                }
              />
              <TextInput
                className="w-full md:w-48"
                placeholder="Password (8+ chars)"
                type="password"
                value={cPassword}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setCPassword(e.target.value)
                }
              />
              <Button
                color="info"
                disabled={
                  creating ||
                  !cEmail.trim() ||
                  !cFirst.trim() ||
                  !cLast.trim() ||
                  cPassword.length < 8
                }
                onClick={createUser}
              >
                {creating ? <Spinner size="sm" /> : 'Create user'}
              </Button>
            </div>
            {createNote && (
              <p className="text-sm text-green-700 dark:text-green-400">
                {createNote}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Current assignments */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">
            Role assignments
          </h2>
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <input
              type="checkbox"
              className="rounded border-gray-300 dark:border-gray-600"
              checked={includeRevoked}
              onChange={(e) => setIncludeRevoked(e.target.checked)}
            />
            Show revoked
          </label>
        </div>

        {loading && <Spinner color="info" />}

        {!loading && data && data.assignments.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            No portal roles assigned yet.
          </p>
        )}

        {!loading && data && data.assignments.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-gray-200 text-gray-500 dark:border-gray-700 dark:text-gray-400">
                <tr>
                  <th className="py-2 pr-4">Role</th>
                  <th className="py-2 pr-4">Member</th>
                  <th className="hidden py-2 pr-4 md:table-cell">Assigned</th>
                  <th className="hidden py-2 pr-4 md:table-cell">By</th>
                  {includeRevoked && (
                    <th className="hidden py-2 pr-4 md:table-cell">Revoked</th>
                  )}
                  <th className="py-2" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {data.assignments.map((a) => (
                  <tr
                    key={a.id}
                    className={
                      a.revokedAtUtc
                        ? 'text-gray-400 dark:text-gray-500'
                        : 'text-gray-900 dark:text-white'
                    }
                  >
                    <td className="py-2 pr-4 font-medium">{a.roleName}</td>
                    <td className="py-2 pr-4">{a.memberName}</td>
                    <td className="hidden py-2 pr-4 md:table-cell">
                      {fmtDate(a.assignedAtUtc)}
                    </td>
                    <td className="hidden py-2 pr-4 md:table-cell">
                      {a.assignedByName}
                    </td>
                    {includeRevoked && (
                      <td className="hidden py-2 pr-4 md:table-cell">
                        {a.revokedAtUtc
                          ? `${fmtDate(a.revokedAtUtc)} by ${a.revokedByName || ''}`
                          : ''}
                      </td>
                    )}
                    <td className="py-2 text-right">
                      {!a.revokedAtUtc && (
                        <Button
                          size="xs"
                          color="failure"
                          onClick={() =>
                            revoke(a.id, `${a.roleName} from ${a.memberName}`)
                          }
                        >
                          Revoke
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Audit history */}
      <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-900">
        <button
          className="text-lg font-medium text-gray-900 hover:underline dark:text-white"
          onClick={toggleAudit}
        >
          {auditOpen ? 'Hide audit history' : 'Show audit history'}
        </button>
        {auditOpen && audit && (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="border-b border-gray-200 text-gray-500 dark:border-gray-700 dark:text-gray-400">
                <tr>
                  <th className="py-2 pr-4">When</th>
                  <th className="py-2 pr-4">Action</th>
                  <th className="py-2 pr-4">Role</th>
                  <th className="hidden py-2 pr-4 md:table-cell">By</th>
                  <th className="hidden py-2 md:table-cell">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-gray-900 dark:divide-gray-800 dark:text-white">
                {audit.map((e) => (
                  <tr key={e.id}>
                    <td className="whitespace-nowrap py-2 pr-4">
                      {fmtDateTime(e.atUtc)}
                    </td>
                    <td className="py-2 pr-4">{e.action}</td>
                    <td className="py-2 pr-4">{e.fieldName}</td>
                    <td className="hidden py-2 pr-4 md:table-cell">
                      {e.actorName}
                    </td>
                    <td className="hidden py-2 md:table-cell">{e.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RoleAdminPage;
